/**
 * PDP integrations & polish
 * -------------------------
 * Consolidates the five inline <script> blocks that previously lived in
 * snippets/product-template.liquid (Swatch King relabelling, size label,
 * fit badge, add-to-cart guard, desktop sticky offset). Loaded product-only
 * from layout/theme.liquid with defer, BEFORE theme.js — deferred scripts
 * execute in document order, so everything here still binds ahead of the
 * theme's variant engine.
 *
 * Each module is a singleton IIFE with a window.__pdp*Bound guard (the same
 * guards the inline versions used) so a re-render can never double-bind.
 * There is one PDP section per page; modules resolve it lazily via
 * getProductSection()/getMobileRoot() so they keep working after the
 * sibling-product swap replaces the section's DOM.
 */
(function () {
  'use strict';

  function getProductSection() {
    return document.querySelector('.product-section[data-section-type="product"]');
  }

  function getMobileRoot() {
    return document.querySelector('.product-section[data-pdp-mobile-root]') || document;
  }

  /* ── Module 1: Swatch King relabelling + Kiwi size-guide relocation ──
     Rewrites third-party Swatch King labels to "OPTION: VALUE", strips the
     invalid role attr from its <style> tags, injects the "WHAT'S MY SIZE?"
     button and relocates the Kiwi app-block trigger next to the size label.
     Swatch King renders asynchronously, so this polls briefly and then
     follows a MutationObserver. */
  (function () {
    if (window.__pdpSwatchRelabelBound) return;
    window.__pdpSwatchRelabelBound = true;

    var rewriteTimer = null;
    var observer = null;

    function scheduleRewrite(delay) {
      clearTimeout(rewriteTimer);
      rewriteTimer = setTimeout(rewriteSwatchLabels, delay || 0);
    }

    // Only ever relocate/relabel a node that is verifiably Kiwi's trigger.
    // After a sibling swap the fresh app-block container does NOT hold the
    // trigger (Kiwi injects on full page load only; theme.js preserves the
    // live node across swaps) — an unverified "first anchor in the host"
    // grab dressed a terms-and-conditions link up as "What's my size?".
    function isKiwiTrigger(el) {
      if (!el) return false;
      var cls = (typeof el.className === 'string' && el.className) || '';
      if (/_ks_|kiwi/i.test(cls)) return true;
      if (el.querySelector && el.querySelector('[class*="_ks_"]')) return true;
      // Text check as last resort — but never accept a link that navigates
      // somewhere real: Kiwi's trigger opens a modal, it doesn't route.
      var href = el.getAttribute && (el.getAttribute('href') || '');
      if (href && href !== '#' && href.indexOf('javascript:') !== 0) return false;
      return /what.?s my size|size (guide|chart)/i.test(el.textContent || '');
    }

    function findKiwiTrigger(root) {
      // Already relocated (by us, or re-rendered by Kiwi after a sibling
      // swap) — find it wherever it currently lives.
      var relocated = document.querySelector('.swatch-size-chart .ks-calculator-modal-link, .swatch-size-chart a, .swatch-size-chart button, .swatch-size-chart [role="button"]');
      if (isKiwiTrigger(relocated)) return relocated;
      // Kiwi renders its trigger as a SPAN (.ks-calculator-modal-link) at its
      // configured position, usually OUTSIDE the app-block host — an a/button
      // scan of the host alone never matches it.
      var rendered = root.querySelector('.ks-calculator-modal-link') || document.querySelector('.ks-calculator-modal-link');
      if (isKiwiTrigger(rendered)) return rendered;
      var host = root.querySelector('.ks-container-app-block') || document.querySelector('.ks-container-app-block');
      if (!host) return null;
      var candidate = host.querySelector('a, button, [role="button"]');
      return isKiwiTrigger(candidate) ? candidate : null;
    }

    function moveKiwiSizeGuide() {
      var root = getMobileRoot();
      var sizeContainer = root.querySelector('[option-name="Size"]');
      if (!sizeContainer) return false;

      var labelRow = sizeContainer.querySelector('.swatch-label-container');
      if (!labelRow) return false;

      if (!labelRow.classList.contains('size-chart-right')) {
        labelRow.classList.add('size-chart-right');
      }

      // Not found / not verifiable yet → return false so the poll/observer
      // retries once Kiwi injects or theme.js restores the preserved node.
      var kiwiTrigger = findKiwiTrigger(root);
      if (!kiwiTrigger) return false;

      var existingMount = labelRow.querySelector('.swatch-size-chart');
      var mount = existingMount || document.createElement('div');
      if (!existingMount) {
        mount.className = 'swatch-size-chart';
        labelRow.appendChild(mount);
      }

      if (kiwiTrigger.parentNode !== mount) {
        mount.appendChild(kiwiTrigger);
      }

      // Equality-guarded: an unconditional textContent write replaces the
      // text node even when identical, and that childList mutation re-fires
      // the observer below — a self-sustaining rewrite loop.
      if (kiwiTrigger.textContent !== "What's my size?") {
        kiwiTrigger.textContent = "What's my size?";
      }
      if (kiwiTrigger.getAttribute('aria-label') !== "What's my size?") {
        kiwiTrigger.setAttribute('aria-label', "What's my size?");
      }
      // CSS keys off this to hide the emptied host block.
      var kiwiHost = root.querySelector('.ks-container-app-block') || document.querySelector('.ks-container-app-block');
      if (kiwiHost && kiwiHost.getAttribute('data-kiwi-relocated') !== 'true') {
        kiwiHost.setAttribute('data-kiwi-relocated', 'true');
      }
      return true;
    }

    function rewriteSwatchLabels() {
      var root = getMobileRoot();

      document.querySelectorAll('style[app="vsk"][role]').forEach(function (styleEl) {
        styleEl.removeAttribute('role');
      });

      var containers = root.querySelectorAll('[option-name], .swatch-single');
      if (!containers.length) return false;
      var anyDone = false;
      containers.forEach(function (container) {
        var optName = (container.getAttribute('option-name') || '').toUpperCase();
        // Strip trailing ": value" if the attribute already includes it (e.g. "Size: M")
        optName = optName.replace(/:.*$/, '').trim();
        if (!optName) {
          // Swatch King no longer outputs option-name; infer from swatch type
          var hasImages = container.querySelector('.swatch-view-image');
          if (hasImages) {
            optName = 'COLOR';
          } else {
            var legend = container.querySelector('legend .swatch-option-name');
            if (legend) {
              var raw = legend.textContent.replace(/\s+/g, ' ').trim();
              if (raw && raw !== 'Selected option') {
                optName = raw.toUpperCase().replace(/:.*$/, '').trim();
              }
            }
          }
        }
        if (!optName) return;
        container.classList.toggle('pdp-option-size', optName === 'SIZE');
        container.classList.toggle('pdp-option-length', optName === 'LENGTH');

        container.querySelectorAll('.swatch-view-item[role="radio"]').forEach(function (item) {
          var visibleLabel = (item.textContent || '').replace(/\s+/g, ' ').trim();
          if (visibleLabel) {
            item.setAttribute('aria-label', visibleLabel);
          }
        });

        var nameEls = container.querySelectorAll('.swatch-option-name');
        // Find selected: prefer .swatch-selected, fall back to aria-checked
        var selected = container.querySelector('.swatch-selected') ||
          container.querySelector('[aria-checked="true"] .swatch-group-selector') ||
          container.querySelector('[aria-checked="true"]');
        if (!nameEls.length || !selected) return;
        var selectedText = selected.querySelector('.swatch-button-title-text');
        var val =
          selected.getAttribute('data-value') ||
          selected.getAttribute('orig-value') ||
          (selectedText ? selectedText.textContent : '') ||
          selected.getAttribute('aria-label') ||
          '';
        val = val.replace(/\s+/g, ' ').trim();
        if (!val) return;
        var html =
          '<span class="swatch-label-prefix">' + optName + ': </span>' +
          '<span class="swatch-label-value">' + val.toUpperCase() + '</span>';
        nameEls.forEach(function (nameEl) {
          if (nameEl.innerHTML !== html) {
            nameEl.innerHTML = html;
          }
          // Guarded: 'class' is in the observer's attributeFilter, and
          // classList.add re-sets the attribute even when already present.
          if (!nameEl.classList.contains('swatch-label-ready')) {
            nameEl.classList.add('swatch-label-ready');
          }
        });

        // Inject SIZE GUIDE button for size swatches
        if (optName === 'SIZE') {
          var labelRow = container.querySelector('.swatch-label-container') || container.querySelector('legend');
          if (labelRow) {
            labelRow.style.display = 'flex';
            labelRow.style.justifyContent = 'space-between';
            labelRow.style.alignItems = 'center';
            var btn = labelRow.querySelector('.swatch-size-guide-btn') || document.createElement('button');
            if (!btn.parentNode) {
              btn.type = 'button';
              btn.className = 'swatch-size-guide-btn';
              labelRow.appendChild(btn);
            }
            if (btn.textContent !== "WHAT'S MY SIZE?") {
              btn.textContent = "WHAT'S MY SIZE?";
            }
            var panel = root.querySelector('[data-pdp-panel][id*="PdpMobileDetails"]') || document.querySelector('[data-pdp-panel][id*="PdpMobileDetails"]');
            if (panel) {
              btn.setAttribute('data-pdp-panel-open', '');
              btn.setAttribute('data-pdp-panel-id', panel.id);
              btn.setAttribute('data-pdp-tab', 'model-size');
              btn.setAttribute('aria-controls', panel.id);
              btn.setAttribute('aria-haspopup', 'dialog');
            }
          }

          // Fit text now rendered in the features section of the details panel.
          // Remove any previously injected swatch fit text.
          var swatchesRoot = container.closest('.swatches');
          if (swatchesRoot) {
            swatchesRoot.querySelectorAll('.swatch-fit-text').forEach(function (el) { el.remove(); });
          }
        }

        anyDone = true;
      });
      return moveKiwiSizeGuide() || anyDone;
    }

    function attachObserver() {
      if (observer) observer.disconnect();
      var root = getMobileRoot();
      // Scope to the tightest container that exists: the Swatch King host,
      // else the product section. NEVER fall back to observing the whole
      // document — with subtree class-attribute observation that meant every
      // class flip anywhere on the page (carousels, drawers, hovers) ran the
      // relabel for the rest of the session. If neither container exists yet
      // (Swatch King renders async), retry shortly; the startup poll keeps
      // labels correct in the interim.
      var swatchRoot = null;
      if (root.querySelector) {
        swatchRoot = root.querySelector('variant-swatch-king');
      }
      if (!swatchRoot && root !== document) swatchRoot = root;
      if (!swatchRoot) swatchRoot = getProductSection();
      if (!swatchRoot) {
        setTimeout(attachObserver, 500);
        return;
      }
      observer = new MutationObserver(function () {
        scheduleRewrite(0);
      });
      observer.observe(swatchRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-checked'] });
    }

    var startupPoll = setInterval(function () {
      if (rewriteSwatchLabels()) {
        clearInterval(startupPoll);
      }
    }, 50);
    setTimeout(function () {
      clearInterval(startupPoll);
      rewriteSwatchLabels();
    }, 10000);

    attachObserver();

    // The sibling-product swap replaces the whole section, so rebind the
    // observer against the fresh nodes (the inline predecessor of this module
    // silently stopped relabelling after a swap).
    document.addEventListener('dfyne:sibling-product:loaded', function () {
      attachObserver();
      scheduleRewrite(0);
    });
    document.addEventListener('shopify:section:load', function () {
      attachObserver();
      scheduleRewrite(0);
    });

    // Update on swatch change
    document.addEventListener('click', function (e) {
      var el = e.target.closest('.swatch-group-selector, .swatch-view-item, .swatch-image, [swatch-inside]');
      if (el && getMobileRoot().contains(el)) {
        scheduleRewrite(100);
      }
    }, true);
    document.addEventListener('change', function (e) {
      if (getMobileRoot().contains(e.target) && e.target.closest('[option-name], .swatch-single')) {
        scheduleRewrite(50);
      }
    }, true);
    document.addEventListener('variant:change', function () {
      scheduleRewrite(50);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        scheduleRewrite(0);
      }, { once: true });
    } else {
      scheduleRewrite(0);
    }
    rewriteSwatchLabels();
  })();

  /* ── Module 2: size label engine ──
     Size label shows "SIZE: <size> | <length>" using the size code (e.g. XS).
     The length option has no label row — its value is appended to the size label. */
  (function () {
    if (window.__pdpSizeLabelBound) return;
    window.__pdpSizeLabelBound = true;
    function handleOf(fs) { return (fs.getAttribute('data-handle') || '').toLowerCase(); }
    function isLength(h) { return h.indexOf('length') > -1 || h.indexOf('inseam') > -1 || h.indexOf('leg') > -1; }
    function update(section) {
      var sizeVal = '', lengthVal = '', valEl = null;
      section.querySelectorAll('.variant-input-wrap').forEach(function (fs) {
        var checked = fs.querySelector('input[data-variant-input]:checked');
        if (!checked) return;
        var h = handleOf(fs);
        if (h.indexOf('size') > -1) {
          sizeVal = checked.value;
          var wrap = fs.closest('.variant-wrapper--labeled');
          if (wrap) { valEl = wrap.querySelector('[data-variant-selected-value]'); }
        } else if (isLength(h)) {
          lengthVal = checked.value;
        }
      });
      if (valEl) {
        valEl.textContent = sizeVal + (lengthVal ? ' | ' + lengthVal : '');
      }
    }
    document.addEventListener('change', function (e) {
      var input = e.target.closest('input[data-variant-input]');
      if (!input) return;
      var section = input.closest('.product-section');
      if (section) { update(section); }
    });
    document.querySelectorAll('.product-section').forEach(update);
  })();

  /* ── Module 3: fit badge ("True to Size") tooltip ──
     Desktop (hover-capable): reveal on hover / :focus-visible; mouse clicks do
     nothing, keyboard activation toggles. Touch: tap toggles, tap elsewhere or
     Escape closes. aria-expanded mirrors the .is-open state. */
  (function () {
    if (window.__pdpFitBadgeBound) return;
    window.__pdpFitBadgeBound = true;
    function hoverCapable() { return window.matchMedia && window.matchMedia('(hover: hover)').matches; }
    function setOpen(badge, open) {
      badge.classList.toggle('is-open', open);
      badge.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    document.addEventListener('click', function (e) {
      var badge = e.target.closest('[data-pdp-fit-badge]');
      var keyboard = false;
      try { keyboard = !!(badge && badge.matches(':focus-visible')); } catch (err) {}
      if (hoverCapable() && !keyboard) return;
      document.querySelectorAll('[data-pdp-fit-badge].is-open').forEach(function (b) {
        if (b !== badge) setOpen(b, false);
      });
      if (badge) { e.preventDefault(); setOpen(badge, !badge.classList.contains('is-open')); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('[data-pdp-fit-badge].is-open').forEach(function (b) { setOpen(b, false); });
      }
    });
  })();

  /* ── Module 4: add-to-cart availability guard ──
     Sold-out sizes stay clickable, but the add-to-cart button reflects the
     selected variant's availability immediately — this runs before theme.js
     in defer order, so a sold-out variant can never be added in the window
     before the theme's variant engine binds. (The server also renders the
     button disabled when the initial variant is unavailable, and Shopify
     rejects unavailable variants at /cart/add.) */
  (function () {
    if (window.__pdpCartGuardBound) return;
    window.__pdpCartGuardBound = true;
    function soldOutLabel() {
      return (window.theme && theme.strings && theme.strings.soldOut) || 'Sold Out';
    }
    function evaluate(section) {
      if (!section) return;
      var jsonEl = section.querySelector('[data-variant-json]');
      var btn = section.querySelector('[data-add-to-cart]');
      var txt = section.querySelector('[data-add-to-cart-text]');
      if (!jsonEl || !btn) return;
      var variants;
      try { variants = JSON.parse(jsonEl.value || jsonEl.textContent || '[]'); } catch (e) { return; }
      var sel = {};
      section.querySelectorAll('input[data-variant-input]:checked').forEach(function (i) {
        sel[(i.getAttribute('data-index') || '').replace('option', '')] = i.value;
      });
      var found = null;
      for (var k = 0; k < variants.length; k++) {
        var v = variants[k];
        if ((!sel['1'] || v.option1 === sel['1']) && (!sel['2'] || v.option2 === sel['2']) && (!sel['3'] || v.option3 === sel['3'])) { found = v; break; }
      }
      var available = !!(found && found.available);
      if (available) {
        btn.disabled = false;
        btn.classList.remove('disabled');
        if (txt && txt.getAttribute('data-default-text')) { txt.textContent = txt.getAttribute('data-default-text'); }
      } else {
        btn.disabled = true;
        btn.classList.add('disabled');
        if (txt) { txt.textContent = soldOutLabel(); }
      }
    }
    document.addEventListener('change', function (e) {
      var input = e.target.closest('input[data-variant-input]');
      if (input) { evaluate(input.closest('.product-section')); }
    }, true);
    function initAll() { document.querySelectorAll('.product-section').forEach(evaluate); }
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initAll); }
    else { initAll(); }
  })();

  /* ── Module 5: blur-up placeholder re-armer ──
     The above-fold desktop slides ship blurred placeholders
     (image-wrap--blur-up, veil in CSS). The PRIMARY binder is inline in
     product-images.liquid — paint-critical, must run at parse. This
     module only re-arms swapped-in sections (sibling swap / section
     re-render), where injected inline scripts never execute. The
     __blurUpArmed flag keeps the two binders from double-binding.
     Fail-open: a veil always clears within 8s. */
  (function () {
    if (window.__pdpBlurUpBound) return;
    window.__pdpBlurUpBound = true;
    function arm(wrap) {
      if (wrap.__blurUpArmed) return;
      wrap.__blurUpArmed = true;
      var img = wrap.querySelector('img');
      var done = function () { wrap.classList.add('blur-up--done'); };
      if (!img) { done(); return; }
      if (img.complete && img.naturalWidth > 0) { done(); return; }
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, 8000);
    }
    function initAll() {
      document.querySelectorAll('.image-wrap--blur-up:not(.blur-up--done)').forEach(arm);
    }
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initAll); }
    else { initAll(); }
    document.addEventListener('dfyne:sibling-product:loaded', initAll);
    document.addEventListener('shopify:section:load', initAll);
  })();

  /* ── Module 6: desktop sticky-offset engine ──
     Sets --pdp-desktop-meta-sticky-top so the meta column parks with its
     bottom in view when the gallery is taller than the viewport. */
  (function () {
    if (window.__pdpStickyInit) return;
    window.__pdpStickyInit = true;

    var desktopQuery = window.matchMedia('(min-width: 769px)');
    var activeObserver = null;
    var raf = null;
    var current = null;

    function initStickyOffset(root) {
      if (!root) return;

      var meta = root.querySelector('.product-single__meta');
      if (!meta) return;

      var detailsCol = root.querySelector('.product-single__details');
      var leftCol = root.querySelector('.product-single__sticky');
      var mediaCol = leftCol ? leftCol.querySelector('.product__main-photos') : null;
      var frame = root.querySelector('.page-content--product > .page-width > .grid');

      function setStickyOffset() {
        if (raf) window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(function () {
          if (!desktopQuery.matches) {
            root.style.removeProperty('--pdp-desktop-meta-sticky-top');
            if (detailsCol) detailsCol.classList.remove('pdp-desktop-sticky-enabled');
            return;
          }

          var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          var mediaHeight = mediaCol ? mediaCol.offsetHeight : 0;
          var detailsHeight = meta.offsetHeight;
          var canStick = detailsCol && mediaHeight > viewportHeight + 1 && mediaHeight > detailsHeight + 1;

          if (!canStick) {
            root.style.removeProperty('--pdp-desktop-meta-sticky-top');
            if (detailsCol) detailsCol.classList.remove('pdp-desktop-sticky-enabled');
            return;
          }

          var stickyTop = Math.min(0, viewportHeight - detailsHeight - 16);
          root.style.setProperty('--pdp-desktop-meta-sticky-top', stickyTop + 'px');
          detailsCol.classList.add('pdp-desktop-sticky-enabled');
        });
      }

      current = setStickyOffset;

      // Re-create the observer against the current section's elements; the
      // previous section's nodes are detached after a swap.
      if (activeObserver) activeObserver.disconnect();
      activeObserver = null;
      if (window.ResizeObserver) {
        activeObserver = new ResizeObserver(setStickyOffset);
        activeObserver.observe(meta);
        if (detailsCol) activeObserver.observe(detailsCol);
        if (mediaCol) activeObserver.observe(mediaCol);
        if (frame) activeObserver.observe(frame);
      }

      setStickyOffset();
    }

    function run() { if (current) current(); }

    window.addEventListener('load', run);
    window.addEventListener('resize', run, { passive: true });
    document.addEventListener('variant:change', run);
    document.addEventListener('dfyne:sibling-product:loaded', function (e) {
      var section = (e.detail && e.detail.productSection) || getProductSection();
      initStickyOffset(section);
    });

    initStickyOffset(getProductSection());
  })();
})();
